import { components, paths } from "@/api-schema";
import {
  PathsV1CasaviContactWhitelistSearchPostParametersQueryOrderByDirection,
  PathsV1CasaviContactWhitelistSearchPostParametersQueryOrderByField,
} from "@/api-schema-enums";
import { DEFAULT_PAGE_SIZE } from "@/common/api/_defaults";
import { useCasaviContactWhitelistDeleteMutation } from "@/common/api/casavi/contact-whitelist/use-casavi-contact-whitelist-delete.mutation";
import { useCasaviContactWhitelistSearchQuery } from "@/common/api/casavi/contact-whitelist/use-casavi-contact-whitelist-search.query";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { RowWithIdentifier } from "@/components/data-table/_helpers/row-identifier";
import { TableFeatures } from "@/components/data-table/types";
import i18next from "i18next";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { createBasePredefinedTable } from "./_components/base-predefined-table";
import {
  BasePredefinedTableProps,
  DataFetchHooks,
  PredefinedTableConfig,
} from "./_components/base-predefined-table.types";
import { casaviContactWhitelistTableDefaultColumns } from "./casavi-contact-whitelist-table-columns";

type CasaviContactWhitelist = components["schemas"]["CasaviContactWhitelist"] & RowWithIdentifier;
type CasaviContactWhitelistSorting = NonNullable<
  paths["/v1/casavi-contact-whitelist/search"]["post"]["parameters"]["query"]["orderBy"]
>;
type CasaviContactWhitelistQueryParams = paths["/v1/casavi-contact-whitelist/search"]["post"]["parameters"]["query"];
type CasaviContactWhitelistRequestBody =
  paths["/v1/casavi-contact-whitelist/search"]["post"]["requestBody"]["content"]["application/json"];

const DEFAULT_COLUMNS = [
  casaviContactWhitelistTableDefaultColumns.columnElement,
  casaviContactWhitelistTableDefaultColumns.columnType,
  casaviContactWhitelistTableDefaultColumns.columnValue,
  casaviContactWhitelistTableDefaultColumns.columnCaseSensitive,
];

const CASAVI_CONTACT_WHITELIST_TABLE_CONFIG: PredefinedTableConfig<CasaviContactWhitelistSorting> = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  defaultSorting: [
    {
      direction: PathsV1CasaviContactWhitelistSearchPostParametersQueryOrderByDirection.Asc,
      field: PathsV1CasaviContactWhitelistSearchPostParametersQueryOrderByField.Value,
    },
  ] as const,
  defaultTableConfig: {
    searchPlaceholder: i18next.t("casavi:SearchValue"),
    features: {
      multiSelection: false,
      pagination: true,
      search: true,
      expansion: false,
    } as TableFeatures,
  } as const,
  defaultRowActions: {
    showEdit: true,
    showDelete: true,
    showView: false,
  } as const,
  sortingMapping: {
    element: ["element"],
    type: ["type"],
    value: ["value"],
    caseSensitive: ["caseSensitive"],
    createdAt: ["createdAt"],
    updatedAt: ["updatedAt"],
  } as const,
} as const;

const CASAVI_CONTACT_WHITELIST_TABLE_QUERY_HOOKS: DataFetchHooks<
  CasaviContactWhitelist,
  CasaviContactWhitelistQueryParams,
  CasaviContactWhitelistRequestBody
> = {
  useSearchQuery: useCasaviContactWhitelistSearchQuery,
} as const;

export interface CasaviContactWhitelistTableProps extends BasePredefinedTableProps<
  CasaviContactWhitelist,
  CasaviContactWhitelistSorting,
  CasaviContactWhitelistQueryParams,
  CasaviContactWhitelistRequestBody
> {}

const BaseCasaviContactWhitelistTable = createBasePredefinedTable({
  config: CASAVI_CONTACT_WHITELIST_TABLE_CONFIG,
  queryHooks: CASAVI_CONTACT_WHITELIST_TABLE_QUERY_HOOKS,
  defaultColumns: DEFAULT_COLUMNS,
});

export function CasaviContactWhitelistTable(props: CasaviContactWhitelistTableProps) {
  const { t } = useTranslation("common");
  const deleteMutation = useCasaviContactWhitelistDeleteMutation();

  const handleDelete = useCallback(
    async (contact: CasaviContactWhitelist) => {
      const contactIdentifier = t("casavi:ValueLabel", { value: contact.value });
      await toastMutationStatus({
        mutationFn: () => deleteMutation.mutateAsync({ uuid: contact.uuid }),
        successMessage: t("ContactWhitelistRemoved", { identifier: contactIdentifier }),
        errorMessage: t("ErrorRemovingContact", { identifier: contactIdentifier }),
      });
    },
    [deleteMutation, t],
  );

  const { name = "casavi-contact-whitelist-table", showCreateButton = true, ...restProps } = props;

  return (
    <BaseCasaviContactWhitelistTable
      searchPlaceholder={t("casavi:SearchValue")}
      name={name}
      showCreateButton={showCreateButton}
      onDelete={handleDelete}
      confirmActions={["delete"]}
      confirmLabels={{
        delete: {
          title: t("RemoveContactFromWhitelist"),
          text: t("ActionCannotBeUndone"),
          confirmButton: t("YesRemove"),
          cancelButton: t("Cancel"),
        },
      }}
      {...restProps}
    />
  );
}
